/**
 * Product Management Page
 *
 * Central hub for managing all catalogue products:
 * - View products with their available stock
 * - Edit product fields and meta tags directly in the catalogue
 * - See available stock per branch
 *
 * Stock is a single number — available — owned by the POS. There is no
 * on-hand / allocated / on-order breakdown any more, and nothing Uber lives
 * here: the Uber Eats side of the catalogue has its own section in the nav.
 * Mode-aware fintech aesthetic via useAdminTheme() / admin-theme.
 */

import { VAPERITE_SELL_IN_APP_TAG } from './vaperite/sellInAppTag';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Lottie from 'lottie-react';
import loadingBikeAnimation from '../assets/lottie/loadingScreenBike.json';
import {
  Box, Grid, Typography, Chip, IconButton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Paper, Pagination,
  alpha, Skeleton,
  Autocomplete, Switch, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon,
  Inventory as StockIcon,
  CheckCircle as CheckIcon, Cancel as CancelIcon,
  Add as AddIcon, Storefront as StorefrontIcon,
  RemoveShoppingCart as OutOfStockIcon,
  ShoppingCart as InStockIcon,
  LocalOffer as PricingIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useAdminTheme, type AdminColorTokens } from '../admin-theme';
import authInstance from '../services/httpClient';
import Logger from '../utils/Logger';

import { APP_BAR_HEIGHT } from '../admin-theme';
import { useScrollToListTop } from '../hooks/useScrollToListTop';
import { useSearchParams } from 'react-router-dom';

// ============================================================================
// Types
// ============================================================================

interface ProductMeta {
  isRestricted: boolean;
  label: string;
  isPopular: boolean;
  isSellableInApp: boolean;
  relatedSku1: string;
  relatedSku2: string;
  relatedSku3: string;
  relatedSku4: string;
  isAnythingElse: boolean;
}

interface StockInfo {
  available: number;
}

/** One store's slice of a product's stock — GET /admin/tools/products/:id/stock. */
interface StockByStoreRow {
  storeId: string;
  storeExternalId: string;
  storeName: string;
  available: number;
  lastRefreshedAt: string | null;
}

interface ManagedProduct {
  id: string;
  sku: string;
  name: string;
  category: string;
  brand: string | null;
  barcode: string;
  /** What the app charges. Vaperite: the Deeliver price (pricing rule, or set by hand). */
  priceTier1: number;
  /** The price as Lightspeed last sent it; null until the product's first sync since 4 Nov 2026. */
  vendPrice?: number | null;
  shortDescription: string;
  description: string;
  status: string;
  sellable: boolean;
  tags: string[];
  lastModifiedOn: string;
  billOfMaterial: boolean;
  imageUrl: string | null;
  allImages: string[];
  meta: ProductMeta;
  stock: StockInfo | null;
  /** True only for Vaperite products: name, brand and barcode are ours to edit (the API rejects them otherwise). */
  editableIdentity?: boolean;
  /** Lightspeed's brand before Brand Mappings (`brand` is the Deeliver brand the mapping made of it). */
  lightspeedBrand?: string | null;
  /**
   * Vaperite field overrides: field → the admin's value and the Lightspeed value it
   * replaced. category values are ids; sourceLabel is the Lightspeed category's name.
   */
  overrides?: Partial<Record<OverrideField, FieldOverride>>;
}

// 'sellable' = Sell in app (meta.isSellableInApp) against Vaperite's woocommerce tag.
type OverrideField = 'name' | 'description' | 'brand' | 'barcode' | 'price' | 'category' | 'sellable';

interface FieldOverride {
  overrideValue: string | null;
  sourceValue: string | null;
  sourceLabel?: string | null;
}

/**
 * Override field → the edit-form key that holds it ("description" is the short
 * description). sellable lives in the form's meta, so it is handled on its own.
 */
const OVERRIDE_FORM_KEY: Record<Exclude<OverrideField, 'sellable'>, keyof ManagedProduct> = {
  name: 'name',
  description: 'shortDescription',
  brand: 'brand',
  barcode: 'barcode',
  price: 'priceTier1',
  category: 'category',
};

/** The Lightspeed value an override replaced, as the tooltip shows it. */
function lightspeedText(field: OverrideField, o: FieldOverride): string {
  const v = field === 'category' ? (o.sourceLabel ?? o.sourceValue) : o.sourceValue;
  if (field === 'sellable') return v === 'true' ? `Lightspeed: tagged ${VAPERITE_SELL_IN_APP_TAG}` : `Lightspeed: no ${VAPERITE_SELL_IN_APP_TAG} tag`;
  if (v === null || v === undefined || v === '') return 'Lightspeed: (empty)';
  if (field === 'price' && Number.isFinite(Number(v))) return `Lightspeed: ${formatCurrency(Number(v))}`;
  return `Lightspeed: ${v}`;
}

/**
 * Fastify schema rejections are {statusCode, error:'Bad Request', message:<detail>};
 * the controllers' own errors are {success:false, error:<text>} with no message.
 */
/** Brand names compared the forgiving way: case, apostrophes, hyphens and spaces ignored. */
const brandKey = (b: string | null | undefined) => String(b ?? '').toLowerCase().replace(/['’\-\s]/g, '');

function apiErrorText(data: any, fallback: string): string {
  if (data?.error && data.error !== 'Bad Request') return data.error;
  return data?.message || data?.error || fallback;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Three summary cards render (each `md: 3`), so the grid row is a full column
 * wider than the cards in it. Right-aligning the cache controls to the grid dropped
 * them over that empty fourth column — which on a 1600px window sat past the right
 * edge of the screen, so "Update Cache" was not reachable without scrolling
 * sideways. Spanning the controls across exactly the occupied columns puts them
 * above the last card ("Out of Stock"), which is where they belong.
 */
const SUMMARY_CARD_SPAN = 3 * 3;

/** Tags beyond this collapse into a "+N" chip, so a heavily-tagged product
 *  can't stretch the row. The rest are listed on hover. */
const MAX_VISIBLE_TAGS = 3;

/**
 * Sentinel for the "Stock at" picker meaning every store summed.
 *
 * Not the empty string: MUI renders an empty Select value as a blank box, and a
 * blank box reads as "nothing chosen" when the cross-branch total is in fact a
 * deliberate choice — and the one most likely to mislead, since it adds 24
 * branches together into a number nobody can act on.
 */
const ALL_STORES = 'all';

/**
 * Label length cap. The DB column is a plain VARCHAR(255) with no validation,
 * so without this you can save a 255-character "label" that wrecks the layout
 * everywhere it renders. A label is a badge, not a sentence.
 */
const MAX_LABEL_LENGTH = 24;

const PAGE_SIZE = 50;

const LOADING_QUOTES = [
  // Classic tech humour
  "Convincing the products to line up nicely...",
  "Teaching barcodes to read themselves...",
  "Negotiating with the catalogue for your data...",
  "Herding digital cats into a spreadsheet...",
  "Counting stock so you don't have to...",
  "Waking up the warehouse elves...",
  "Politely asking the database for products...",
  "Loading faster than your morning coffee...",
  "Turning ones and zeros into groceries...",
  "Syncing inventory across the multiverse...",
  "Bribing the server hamsters to run faster...",
  "Alphabetising things nobody will sort alphabetically...",
  "Refreshing the product vibes...",
  "Asking each product for ID verification...",
  "Calculating how many avocados fit in a truck...",
  "Untangling the spaghetti of stock levels...",
  "Running diagnostics on the snack aisle...",
  "Downloading more RAM for this page...",
  "Consulting the oracle of inventory...",
  "Making sure nothing expired while you blinked...",
  // Delivery themed
  "Dispatching tiny delivery robots to fetch your data...",
  "Your products are stuck in traffic on the N2...",
  "Waiting for the delivery guy to find the right address...",
  "Products are taking the scenic route...",
  "GPS says 3 minutes but we all know better...",
  "Your order is out for delivery... to this screen...",
  "Recalculating the fastest route to your products...",
  // South African flavour
  "Load shedding the unnecessary data...",
  "Eskom hasn't cut us off yet, almost there...",
  "Just now... or now now... definitely soon...",
  "Braaiing some fresh data for you...",
  "Checking if the robots are still standing...",
  "Products are queuing at Home Affairs...",
  "Waiting for the minibus taxi to arrive with your data...",
  "The data is coming just after the traffic light...",
  // Warehouse humour
  "Someone left the forklift keys in the wrong place...",
  "The night shift forgot to count the bananas...",
  "Double-checking that nobody ate the samples...",
  "Making sure the eggs aren't on top of the bread...",
  "Reorganising aisle 7 for the fifth time today...",
  "The scanner batteries died again...",
  "Playing Tetris with the stock room...",
  "Found the missing pallet, loading now...",
  // Existential
  "Do products dream of electric shopping carts?",
  "Wondering if the SKUs are happy with their names...",
  "Each product is a unique snowflake of commerce...",
  "In a parallel universe, the products load you...",
  "The products are having a team meeting...",
  "Asking the products to stop gossiping and load...",
  // Tech ops
  "The cloud is just someone else's computer and it's thinking...",
  "SELECT * FROM patience WHERE user = 'you'...",
  "Have you tried turning the products off and on again?",
  "This page brought to you by caffeine and optimism...",
  "Promise.all(products) is still pending...",
  "The API is on its second espresso...",
  "Ctrl+Alt+Delivering your products...",
  "Error 418: I'm a teapot... just kidding, loading...",
  "git pull origin products --force...",
  "sudo make me a product list...",
  "while (!loaded) { patience++; }",
  // Food related
  "Checking if the avocados are ripe enough to display...",
  "The milk is still deciding which expiry date to wear...",
  "Organic, free-range data takes longer to load...",
  "Marinating the product data in premium algorithms...",
  "Farm to table, but for spreadsheets...",
  "The sourdough starter of product data needs time...",
];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);


// ============================================================================
// GlowCard Component
// ============================================================================

interface GlowCardProps {
  icon: React.ReactElement;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
  colors: AdminColorTokens;
  /** Makes the card a filter button. `selected` draws it in its own colour so
   *  the active filter is visible without reading the dropdown underneath. */
  onClick?: () => void;
  selected?: boolean;
}

function GlowCard({ icon, label, value, color, subtitle, colors, onClick, selected }: GlowCardProps) {
  return (
    <Paper
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-pressed={onClick ? !!selected : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={{
        p: 2.5,
        height: '100%',
        bgcolor: selected ? colors.bgElevated : colors.bgCard,
        border: `1px solid ${selected ? alpha(color, 0.6) : colors.border}`,
        boxShadow: selected ? `0 0 30px ${alpha(color, 0.18)}, inset 0 0 30px ${alpha(color, 0.06)}` : 'none',
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        '&:focus-visible': { outline: `2px solid ${alpha(color, 0.8)}`, outlineOffset: 2 },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '&:hover': {
          bgcolor: colors.bgElevated,
          borderColor: alpha(color, 0.4),
          boxShadow: `0 0 30px ${alpha(color, 0.15)}, inset 0 0 30px ${alpha(color, 0.05)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
          flexShrink: 0,
          boxShadow: `0 0 15px ${alpha(color, 0.1)}`,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 26 } } as any)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 800, lineHeight: 1.2, mt: 0.25 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Stock Color Helper
// ============================================================================

function getStockColor(available: number | undefined, colors: AdminColorTokens): string {
  if (available === undefined || available === null) return colors.textMuted;
  if (available <= 0) return colors.error;
  if (available <= 5) return colors.warning;
  return colors.success;
}

// ============================================================================
// Filters
// ============================================================================

/**
 * Filter that's both a dropdown and a text input — pick from the known options
 * or type a value straight in. Used for Category and Tag, which can have long
 * option lists that are painful to scroll.
 */
function FilterAutocomplete({ label, placeholder, options, value, onChange, colors }: {
  label: string;
  placeholder: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  colors: any;
}) {
  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={value || null}
      onChange={(_, v) => onChange(v ?? '')}
      onInputChange={(_, v, reason) => {
        // Typing filters live; clearing resets. 'reset' fires on select, which
        // onChange already handled.
        if (reason === 'input' || reason === 'clear') onChange(v ?? '');
      }}
      sx={{ minWidth: 180 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          sx={{
            bgcolor: colors.bgElevated,
            '& .MuiInputBase-input': { color: colors.textPrimary },
            '& .MuiInputLabel-root': { color: colors.textSecondary },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              color: colors.textSecondary,
            },
          }}
        />
      )}
    />
  );
}

/** Yes / No / All filter for a boolean flag. */
function YesNoFilter({ label, value, onChange, colors }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  colors: any;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel sx={{ color: colors.textSecondary }}>{label}</InputLabel>
      <Select
        value={value}
        onChange={e => onChange(e.target.value as string)}
        label={label}
        sx={{
          bgcolor: colors.bgElevated, color: colors.textPrimary,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
          '& .MuiSelect-icon': { color: colors.textSecondary },
        }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </Select>
    </FormControl>
  );
}

/**
 * Boolean flag cell — green Yes / grey No.
 * Shared by Sellable, Restricted and Popular so a true flag reads identically
 * everywhere, rather than each column inventing its own badge.
 */
function YesNoChip({ on, colors }: { on: boolean; colors: any }) {
  return (
    <Chip
      label={on ? 'Yes' : 'No'}
      size="small"
      sx={{
        bgcolor: alpha(on ? colors.success : colors.textMuted, 0.12),
        color: on ? colors.success : colors.textMuted,
        fontSize: '0.65rem',
        height: 20,
      }}
    />
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * @param vendOrg  Scope the page to one retailer's catalogue. Omitted = every
 *   product, which is the existing /products page. The Vaperite entry passes
 *   'vaperite' so the same tool serves both — one page means the DB-only vs
 *   pushed-to-Vend rules can never drift apart between them.
 */
const ProductManagement: React.FC<{ vendOrg?: 'deeliver' | 'vaperite' }> = ({ vendOrg }) => {
  const { colors } = useAdminTheme();
  const notify = useNotify();

  // Data state
  const [products, setProducts] = useState<ManagedProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ totalProducts: 0, inStock: 0, outOfStock: 0 });
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true); // first load ever (cache may be cold)

  // Filter state
  // Seeded from ?search= so other pages can deep-link a product here — e.g. the
  // dashboard's Top Selling Items quick view.
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState<string>('all');
  /**
   * Which store the Stock column reports. Empty = every store summed, which is a
   * cross-branch total nobody can act on, so it is offered as a deliberate
   * choice rather than being the silent default it used to be.
   */
  // 'all' rather than '': an empty MUI Select value renders a blank box, so the
  // default reads as "no choice made" when it is in fact a deliberate one.
  const [storeFilter, setStoreFilter] = useState<string>(ALL_STORES);
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [sellableFilter, setSellableFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [popularFilter, setPopularFilter] = useState<string>('');
  const [labelFilter, setLabelFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const listTopRef = useScrollToListTop(page, { below: stickyHeaderRef });
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Dialog state
  const [editProduct, setEditProduct] = useState<ManagedProduct | null>(null);

  // Per-store stock breakdown (the green "Stock" button in the edit dialog header).
  const [stockByStoreFor, setStockByStoreFor] = useState<ManagedProduct | null>(null);
  const [stockByStore, setStockByStore] = useState<StockByStoreRow[] | null>(null);
  const [stockByStoreLoading, setStockByStoreLoading] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState<any>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  /** The override field whose "Follow Lightspeed again" is in flight. */
  const [followingField, setFollowingField] = useState<OverrideField | null>(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{ payload: any; changes: string[] } | null>(null);

  // YML product search state
  const [ymlSearching, setYmlSearching] = useState<string | null>(null); // which YML slot is open
  const [ymlQuery, setYmlQuery] = useState('');
  const [ymlResults, setYmlResults] = useState<Array<{ sku: string; name: string }>>([]);
  const [ymlLoading, setYmlLoading] = useState(false);

  // ── Search Debounce ─────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Data Fetching ───────────────────────────────────────────────────────

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        limit: PAGE_SIZE,
        sortBy,
        sortDir,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (categoryFilter) params.category = categoryFilter;
      if (stockFilter !== 'all') params.stockStatus = stockFilter;
      if (sellableFilter) params.sellableInApp = sellableFilter;
      if (tagFilter) params.tag = tagFilter;
      if (popularFilter) params.isPopular = popularFilter;
      if (labelFilter) params.hasLabel = labelFilter;
      // Scope is a property of the page, not a filter the user can clear.
      if (vendOrg) params.vendOrg = vendOrg;
      // Scopes stock, the stock filter AND the summary counts to one store.
      if (storeFilter !== ALL_STORES) params.storeId = storeFilter;

      const { data } = await authInstance.get('admin/tools/products', { params });
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setSummary(data.summary || { totalProducts: 0, inStock: 0, outOfStock: 0 });
    } catch (err: any) {
      Logger.error('Failed to fetch products:', err);
      notify('Failed to load products', { type: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [page, debouncedSearch, categoryFilter, tagFilter, stockFilter, storeFilter, sellableFilter, popularFilter, labelFilter, sortBy, sortDir, vendOrg, notify]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await authInstance.get('admin/tools/products/categories', { params: vendOrg ? { vendOrg } : {} });
      setCategories(data.data || []);
    } catch (err: any) {
      Logger.error('Failed to fetch categories:', err);
    }
  }, [vendOrg]);

  /** Stores, for scoping the Stock column. Active ones only — you cannot sell
   *  from a deactivated branch, so offering it would only invite a confusing zero. */
  const fetchStores = useCallback(async () => {
    try {
      // On a retailer's page (Vaperite Products), only that retailer's stores.
      const { data } = await authInstance.get('admin/stores', { params: vendOrg ? { vendOrg } : {} });
      const rows = (data.data || data || []) as Array<{ id: string; name: string; isActive?: boolean }>;
      setStores(
        rows
          .filter((st) => st.isActive !== false)
          .map((st) => ({ id: st.id, name: st.name }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (err: any) {
      Logger.error('Failed to fetch stores:', err);
    }
  }, [vendOrg]);

  /** Distinct tags across the catalogue — the options behind the Tag filter. */
  const fetchTags = useCallback(async () => {
    try {
      const { data } = await authInstance.get('admin/tools/products/tags', { params: vendOrg ? { vendOrg } : {} });
      setTags(data.data || []);
    } catch (err: any) {
      Logger.error('Failed to fetch tags:', err);
    }
  }, [vendOrg]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchStores();
  }, [fetchCategories, fetchTags, fetchStores]);

  // ── Actions ─────────────────────────────────────────────────────────────

  /**
   * The three summary cards double as stock filters. Clicking the one that is
   * already active drops back to "all" so a card is never a dead end; the
   * Stock dropdown still exists for Low Stock, which has no card.
   */
  const selectStockCard = (value: 'all' | 'in-stock' | 'out-of-stock') => {
    setStockFilter(stockFilter === value && value !== 'all' ? 'all' : value);
    setPage(1);
  };

  // ── Vaperite pricing rule (Jason, 4 Nov 2026): Deeliver price = Lightspeed price ÷ divisor, up to 50c ──
  interface PricingRuleState { enabled: boolean; divisor: number; ruled: number; handSet: number; noVendPrice: number }
  const [pricingRule, setPricingRule] = useState<PricingRuleState | null>(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingBusy, setPricingBusy] = useState(false);
  useEffect(() => {
    if (vendOrg !== 'vaperite') return;
    authInstance.get('admin/commands/vaperite-pricing-rule')
      .then((res: any) => setPricingRule(res?.data?.rule ?? null))
      .catch((err: any) => Logger.error('Failed to load the Vaperite pricing rule:', err));
  }, [vendOrg]);
  /** What the rule gives for a Lightspeed price (the backend's arithmetic, for the edit dialog's hint). */
  const rulePrice = (vendPrice: number | null | undefined): number | null => {
    if (vendPrice == null || !pricingRule) return null;
    if (!pricingRule.enabled) return vendPrice;
    const cents = Math.round((vendPrice / pricingRule.divisor) * 100);
    return Math.ceil(cents / 50) * 50 / 100;
  };
  const switchPricingRule = async (enabled: boolean) => {
    setPricingBusy(true);
    try {
      const { data } = await authInstance.patch('admin/commands/vaperite-pricing-rule', { enabled });
      const rule = data?.rule;
      setPricingRule(rule ?? null);
      notify(`Deeliver pricing rule ${enabled ? 'on' : 'off'}: ${rule?.repriced ?? 0} product${rule?.repriced === 1 ? '' : 's'} repriced${rule?.handSet ? `, ${rule.handSet} set by hand left alone` : ''}`, { type: 'success' });
      fetchProducts();
    } catch (err: any) {
      notify(apiErrorText(err?.response?.data, 'Could not change the pricing rule'), { type: 'error' });
    } finally {
      setPricingBusy(false);
    }
  };

  // ── Edit Dialog ─────────────────────────────────────────────────────────

  const openEditDialog = (product: ManagedProduct) => {
    setEditProduct(product);
    setEditForm({
      sku: product.sku,
      name: product.name,
      brand: product.brand ?? '',
      barcode: product.barcode ?? '',
      priceTier1: product.priceTier1,
      // "Don't override" = hand-set (a field override) and syncs ignore it, both orgs (Jason, 5 Nov 2026)
      priceLocked: !!product.overrides?.price,
      descriptionLocked: !!product.overrides?.description,
      // The description the app shows (older rows only had the Lightspeed text in short_description)
      description: product.description || product.shortDescription || '',
      category: product.category,
      tags: product.tags.join(', '),
      meta: { ...product.meta },
    });
  };

  const buildEditDiff = (): { payload: any; changes: string[] } | null => {
    if (!editProduct) return null;
    const payload: any = {};
    const changes: string[] = [];

    // Jason, 5 Nov 2026: only the Deeliver price and the description (each with its "Don't
    // override" tick), the meta tags, the label and the related products are edited here, for
    // Vaperite's products and ours alike. Everything else comes from Lightspeed through Item Staging.
    // The tick is what gets saved: a changed value is sent with the tick as it stands (unticked =
    // follow Lightspeed, the typed value dropped), and ticking or unticking alone sends the lock.
    const shownDescription = editProduct.description || editProduct.shortDescription || '';
    const priceWasLocked = !!editProduct.overrides?.price;
    const priceTyped = editForm.priceTier1 !== '' && editForm.priceTier1 != null;
    const priceChanged = priceTyped && Number(editForm.priceTier1) !== Number(editProduct.priceTier1);
    const priceTick = !!editForm.priceLocked;
    if (!priceTick && (priceWasLocked || priceChanged)) {
      if (priceWasLocked) {
        payload.priceLocked = false;
        changes.push(vendOrg === 'vaperite'
          ? 'Deeliver price: no longer set by hand. Follows Lightspeed and the pricing rule again'
          : 'Deeliver price: no longer set by hand. Follows Lightspeed again');
      }
    } else if (priceTick && (priceChanged || !priceWasLocked)) {
      payload.priceLocked = true;
      if (priceChanged) {
        payload.priceTier1 = Number(editForm.priceTier1);
        changes.push(`Deeliver price: ${formatCurrency(editProduct.priceTier1)} → ${formatCurrency(Number(editForm.priceTier1))} (set by hand: syncs will not change it)`);
      } else {
        changes.push('Deeliver price: set by hand (syncs will not change it)');
      }
    }
    const descriptionWasLocked = !!editProduct.overrides?.description;
    const description = String(editForm.description ?? '');
    const descriptionChanged = description.trim() !== shownDescription.trim();
    const descriptionTick = !!editForm.descriptionLocked;
    if (!descriptionTick && (descriptionWasLocked || descriptionChanged)) {
      if (descriptionWasLocked) {
        payload.descriptionLocked = false;
        changes.push('Description: no longer set by hand. Follows Lightspeed again');
      }
    } else if (descriptionTick && (descriptionChanged || !descriptionWasLocked)) {
      payload.descriptionLocked = true;
      if (descriptionChanged) {
        payload.description = description;
        changes.push('Description updated (set by hand: syncs will not change it)');
      } else {
        changes.push('Description: set by hand (syncs will not change it)');
      }
    }

    const metaDiff: any = {};
    const orig = editProduct.meta;
    const form = editForm.meta;
    if (form.isRestricted !== orig.isRestricted) { metaDiff.isRestricted = form.isRestricted; changes.push(`Restricted: ${orig.isRestricted ? 'Yes' : 'No'} → ${form.isRestricted ? 'Yes' : 'No'}`); }
    if (form.label !== orig.label) { metaDiff.label = form.label; changes.push(`Label: "${orig.label || '—'}" → "${form.label || '—'}"`); }
    if (form.isPopular !== orig.isPopular) { metaDiff.isPopular = form.isPopular; changes.push(`New & Popular: ${orig.isPopular ? 'Yes' : 'No'} → ${form.isPopular ? 'Yes' : 'No'}`); }
    if (form.isSellableInApp !== orig.isSellableInApp) { metaDiff.isSellableInApp = form.isSellableInApp; changes.push(`Sellable in App: ${orig.isSellableInApp ? 'Yes' : 'No'} → ${form.isSellableInApp ? 'Yes' : 'No'}`); }
    if (form.relatedSku1 !== orig.relatedSku1) { metaDiff.relatedSku1 = form.relatedSku1; changes.push(`YML 1: ${orig.relatedSku1 || '—'} → ${form.relatedSku1 || '—'}`); }
    if (form.relatedSku2 !== orig.relatedSku2) { metaDiff.relatedSku2 = form.relatedSku2; changes.push(`YML 2: ${orig.relatedSku2 || '—'} → ${form.relatedSku2 || '—'}`); }
    if (form.relatedSku3 !== orig.relatedSku3) { metaDiff.relatedSku3 = form.relatedSku3; changes.push(`YML 3: ${orig.relatedSku3 || '—'} → ${form.relatedSku3 || '—'}`); }
    if (form.relatedSku4 !== orig.relatedSku4) { metaDiff.relatedSku4 = form.relatedSku4; changes.push(`YML 4: ${orig.relatedSku4 || '—'} → ${form.relatedSku4 || '—'}`); }
    if (form.isAnythingElse !== orig.isAnythingElse) { metaDiff.isAnythingElse = form.isAnythingElse; changes.push(`Anything Else: ${orig.isAnythingElse ? 'Yes' : 'No'} → ${form.isAnythingElse ? 'Yes' : 'No'}`); }
    if (Object.keys(metaDiff).length > 0) payload.meta = metaDiff;

    if (Object.keys(payload).length === 0) return null;
    return { payload, changes };
  };

  const handleEditSave = () => {
    const diff = buildEditDiff();
    if (!diff) {
      notify('No changes to save', { type: 'info' });
      return;
    }
    setConfirmPayload(diff);
  };

  /**
   * "Follow Lightspeed again": drop the override; the server puts the Lightspeed
   * value back. The dialog shows the fresh product (other unsaved edits stay in
   * the form) and the list refreshes.
   */
  const handleFollowLightspeed = async (field: OverrideField) => {
    if (!editProduct) return;
    setFollowingField(field);
    try {
      const { data } = await authInstance.delete(`admin/tools/products/${editProduct.id}/overrides/${field}`);
      const fresh: ManagedProduct | undefined = data?.data;
      if (fresh) {
        setEditProduct(fresh);
        if (field === 'sellable') {
          setEditForm((form: any) => ({ ...form, meta: { ...form.meta, isSellableInApp: fresh.meta.isSellableInApp } }));
        } else {
          const key = OVERRIDE_FORM_KEY[field];
          setEditForm((form: any) => ({ ...form, [key]: key === 'brand' || key === 'barcode' ? (fresh[key] ?? '') : fresh[key] }));
        }
      }
      notify('Following Lightspeed again', { type: 'success' });
      fetchProducts();
    } catch (err: any) {
      notify(apiErrorText(err?.response?.data, 'Failed to remove the override'), { type: 'error' });
    } finally {
      setFollowingField(null);
    }
  };

  /**
   * An override on a field that is read-only here now (name, barcode, brand, category: set by hand
   * before 5 Nov 2026) stays visible, with "Follow Lightspeed again" to release it.
   */
  const lockedChip = (field: OverrideField) => {
    const o = overrideProps(field).override;
    if (!o) return null;
    return (
      <Stack direction="row" spacing={1} alignItems="center" data-field={field}>
        <Tooltip title={o.tooltip}>
          <Chip data-testid={`overridden-${field}`} label="Overridden" size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(colors.warning, 0.12), color: colors.warning, border: `1px solid ${alpha(colors.warning, 0.3)}` }} />
        </Tooltip>
        <Button size="small" disabled={o.busy} onClick={o.onFollow}
          sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0, minWidth: 0, color: colors.info }}>
          Follow Lightspeed again
        </Button>
      </Stack>
    );
  };

  /** EditableField's override props for a field, when the product overrides it. */
  const overrideProps = (field: OverrideField) => {
    const o = editProduct?.overrides?.[field];
    if (!o) return {};
    return {
      override: {
        field,
        tooltip: lightspeedText(field, o),
        busy: followingField === field,
        onFollow: () => handleFollowLightspeed(field),
      },
    };
  };

  const handleConfirmSave = async () => {
    if (!editProduct || !confirmPayload) return;
    setEditSaving(true);
    setConfirmPayload(null);
    try {
      await authInstance.put(`admin/tools/products/${editProduct.id}`, confirmPayload.payload);
      notify('Product updated', { type: 'success' });
      setEditProduct(null);
      fetchProducts();
    } catch (err: any) {
      notify(apiErrorText(err?.response?.data, 'Failed to update product'), { type: 'error' });
    } finally {
      setEditSaving(false);
    }
  };

  // Set/replace the product's default image — upload a file OR submit a URL → POST /admin/product-attachments.
  const handleSetImage = async (file?: File) => {
    if (!editProduct) return;
    const url = newImageUrl.trim();
    if (!file && !url) return;
    setImageUploading(true);
    try {
      if (file) {
        const form = new FormData();
        form.append('productId', editProduct.id);
        form.append('image', file);
        await authInstance.post('admin/product-attachments', form, { headers: { 'Content-Type': undefined } });
      } else {
        await authInstance.post('admin/product-attachments', { productId: editProduct.id, url });
      }
      setNewImageUrl('');
      notify('Image saved — it will appear once processing finishes', { type: 'success' });
      fetchProducts();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Failed to set image', { type: 'error' });
    } finally {
      setImageUploading(false);
    }
  };

  /**
   * Open the per-store stock breakdown for a product.
   *
   * Reads `product_stock_by_store` server-side — the same materialized view the
   * app serves from — so this shows what a shopper at each store would see, not
   * a POS-side total that can disagree with it.
   */
  const openStockByStore = async (product: ManagedProduct) => {
    setStockByStoreFor(product);
    setStockByStore(null);
    setStockByStoreLoading(true);
    try {
      const { data } = await authInstance.get(`admin/tools/products/${product.id}/stock`);
      setStockByStore(data.data || []);
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Failed to load stock by store', { type: 'error' });
      setStockByStore([]);
    } finally {
      setStockByStoreLoading(false);
    }
  };

  // ── Sort Handler ────────────────────────────────────────────────────────

  const handleSort = (col: string) => {
    // Vaperite's Deeliver Price (Jason, 4 Nov 2026): high to low, then low to high, then the
    // prices set by hand ("Don't override") on top, then round again.
    if (vendOrg === 'vaperite' && col === 'price') {
      if (sortBy === 'price' && sortDir === 'desc') { setSortDir('asc'); }
      else if (sortBy === 'price' && sortDir === 'asc') { setSortBy('priceLocked'); setSortDir('asc'); }
      else { setSortBy('price'); setSortDir('desc'); }
      setPage(1);
      return;
    }
    if (sortBy === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortArrow = (col: string) => (col === 'price' && sortBy === 'priceLocked') ? ' (SET first)' : sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // ── YML Product Search ──────────────────────────────────────────────────

  const searchYmlProducts = useCallback(async (query: string) => {
    if (!query.trim() || query.trim().length < 2) {
      setYmlResults([]);
      return;
    }
    setYmlLoading(true);
    try {
      // Scoped to the same catalogue as the page. Without this the Vaperite page
      // would offer Deeliver products as related items — a cross-retailer link
      // that makes no sense to a customer and that neither sync would maintain.
      const { data } = await authInstance.get('admin/tools/products', {
        params: { search: query.trim(), limit: 8, page: 1, ...(vendOrg ? { vendOrg } : {}) },
      });
      setYmlResults((data.products || []).map((p: any) => ({ sku: p.sku, name: p.name })));
    } catch {
      setYmlResults([]);
    } finally {
      setYmlLoading(false);
    }
  }, [vendOrg]);

  useEffect(() => {
    if (!ymlSearching) return;
    const timer = setTimeout(() => searchYmlProducts(ymlQuery), 300);
    return () => clearTimeout(timer);
  }, [ymlQuery, ymlSearching, searchYmlProducts]);

  const openYmlSearch = (slot: string) => {
    setYmlSearching(slot);
    setYmlQuery('');
    setYmlResults([]);
  };

  const YML_SLOTS = ['relatedSku1', 'relatedSku2', 'relatedSku3', 'relatedSku4'] as const;

  /**
   * Assign a product, then move to the next EMPTY slot and keep the search open,
   * so one search session can fill all four. Closing after every pick meant
   * re-opening and re-typing the same query four times.
   *
   * Closes only when nothing is left to fill — otherwise the picker would sit
   * open with nowhere to put the next choice.
   */
  const selectYmlProduct = (slot: string, sku: string) => {
    setEditForm((prev: any) => {
      const meta = { ...prev.meta, [slot]: sku };
      const nextFree = YML_SLOTS.find((sl) => sl !== slot && !meta[sl]);
      // Scheduled rather than set directly: this runs inside the state updater,
      // and the next slot depends on the meta we are about to commit.
      setTimeout(() => {
        if (nextFree) {
          setYmlSearching(nextFree);
          // Query and results are kept deliberately — the same search usually
          // yields the next related product too.
        } else {
          setYmlSearching(null);
          setYmlQuery('');
          setYmlResults([]);
        }
      }, 0);
      return { ...prev, meta };
    });
  };

  /** SKUs already sitting in a slot, so the picker can show them as taken. */
  const assignedYmlSkus = React.useMemo(
    () => new Set(YML_SLOTS.map((sl) => editForm.meta?.[sl]).filter(Boolean)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editForm.meta?.relatedSku1, editForm.meta?.relatedSku2, editForm.meta?.relatedSku3, editForm.meta?.relatedSku4],
  );

  const clearYmlSlot = (slot: string) => {
    setEditForm((prev: any) => ({ ...prev, meta: { ...prev.meta, [slot]: '' } }));
  };

  // ── Render ──────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Loading quote rotation ─────────────────────────────────────────────
  const [quoteIndex, setQuoteIndex] = useState(() => Math.floor(Math.random() * LOADING_QUOTES.length));
  const quoteIntervalRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    if (initialLoad && loading) {
      quoteIntervalRef.current = setInterval(() => {
        setQuoteIndex((prev) => (prev + 1) % LOADING_QUOTES.length);
      }, 3500);
    }
    return () => clearInterval(quoteIntervalRef.current);
  }, [initialLoad, loading]);

  // ── Initial Loading Screen (cold cache) ───────────────────────────────
  if (initialLoad && loading) {
    return (
      <Box sx={{
        bgcolor: colors.bgDeepest, minHeight: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
      }}>
        <Lottie
          animationData={loadingBikeAnimation}
          loop
          autoPlay
          style={{ width: 260, height: 260 }}
        />
        <Box sx={{ textAlign: 'center', mt: -1 }}>
          <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1.5 }}>
            Loading Product Catalogue
          </Typography>
          <Typography
            key={quoteIndex}
            variant="body1"
            sx={{
              color: colors.textSecondary,
              fontStyle: 'italic',
              minHeight: 28,
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(6px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
              animation: 'fadeIn 0.5s ease-out',
            }}
          >
            {LOADING_QUOTES[quoteIndex]}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(colors.textMuted, 0.6), display: 'block', mt: 2 }}>
            Fetching products — this may take a moment on first load
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    // overflowX: 'clip' — NOT 'hidden'. `hidden` turns this into a scroll
    // container, which silently breaks the sticky header inside it. `clip`
    // stops the page drifting sideways without that side effect.
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh', p: 3, overflowX: 'clip', maxWidth: '100%' }}>
      {/* ── Sticky header: title, actions, summary cards and filters stay put
           while the product table scrolls beneath them. ──────────────────── */}
      <Box
        ref={stickyHeaderRef}
        sx={{
          position: 'sticky',
          top: APP_BAR_HEIGHT,
          zIndex: 10,
          bgcolor: colors.bgDeepest,
          pt: 1,
          // No negative margins here: `mx: -3` with `px: 3` makes this 48px wider
          // than its parent, which is itself enough to scroll the page sideways.
          width: '100%',
          minWidth: 0,
        }}
      >
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 800 }}>
            {vendOrg === 'vaperite' ? 'Vaperite Products' : vendOrg === 'deeliver' ? 'Deeliver Products' : 'Products'}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
            Manage products and their available stock
          </Typography>
        </Box>
      </Box>


      {/* Vaperite: the pricing rule, on its own row directly above the counters. The row is a grid
          matching the counters below it (see SUMMARY_CARD_SPAN), so its right edge lines up with the
          last card. Deeliver and the all-products page have no buttons here (Jason, 5 Nov 2026): the
          Vend sync lives in Deeliver Sync, and Refresh from Uber belongs to the Uber Menu page. */}
      {vendOrg === 'vaperite' && (
        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid
            size={{ xs: 12, md: SUMMARY_CARD_SPAN }}
            sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<PricingIcon />}
              onClick={() => setPricingOpen(true)}
              sx={{ borderColor: pricingRule?.enabled ? alpha(colors.success, 0.6) : colors.border, color: pricingRule?.enabled ? colors.success : colors.textSecondary, textTransform: 'none' }}
            >
              Pricing Rule{pricingRule ? (pricingRule.enabled ? ': on' : ': off') : ''}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* ── Vaperite pricing rule ── */}
      {vendOrg === 'vaperite' && (
        <Dialog open={pricingOpen} onClose={() => !pricingBusy && setPricingOpen(false)} maxWidth="sm" fullWidth
          slotProps={{ paper: { sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 3, backgroundImage: 'none' } } }}>
          <DialogTitle sx={{ color: colors.textPrimary, fontWeight: 700 }}>Deeliver Pricing Rule</DialogTitle>
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Switch
                checked={!!pricingRule?.enabled}
                disabled={pricingBusy || !pricingRule}
                onChange={(e) => switchPricingRule(e.target.checked)}
                slotProps={{ input: { role: 'switch', 'aria-label': 'Deeliver pricing rule' } }}
              />
              <Typography sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                {pricingRule ? (pricingRule.enabled ? 'On' : 'Off') : 'Loading…'}
              </Typography>
              {pricingBusy && <CircularProgress size={16} />}
            </Stack>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
              Implements a pricing rule which takes your Lightspeed retail price and divides it by {pricingRule?.divisor ?? 0.885} and rounds up to 50 cents.
              That builds Deeliver&apos;s 10% commission on the cart subtotal (inc VAT), plus the VAT on that commission, into the Deeliver price.
              When toggled off, all Deeliver prices match Lightspeed prices.
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
              A product whose Deeliver price is set by hand ("Don't override" ticked when you click into it, shown as (SET) in the list) is left alone either way, until you untick it.
            </Typography>
            {pricingRule && (
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
                {pricingRule.ruled.toLocaleString()} product{pricingRule.ruled === 1 ? '' : 's'} priced by the rule
                {pricingRule.handSet ? ` · ${pricingRule.handSet.toLocaleString()} set by hand (SET)` : ''}
                {pricingRule.noVendPrice ? ` · ${pricingRule.noVendPrice.toLocaleString()} waiting for a Lightspeed price (next sync)` : ''}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setPricingOpen(false)} disabled={pricingBusy} sx={{ color: colors.textSecondary, textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {/* ── Summary Cards ───────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <GlowCard
            icon={<StorefrontIcon />}
            label="Total Products"
            value={summary.totalProducts}
            color={colors.info}
            colors={colors}
            selected={stockFilter === 'all'}
            onClick={() => selectStockCard('all')}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <GlowCard
            icon={<InStockIcon />}
            label="In Stock"
            value={summary.inStock}
            color={colors.success}
            colors={colors}
            selected={stockFilter === 'in-stock'}
            onClick={() => selectStockCard('in-stock')}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <GlowCard
            icon={<OutOfStockIcon />}
            label="Out of Stock"
            value={summary.outOfStock}
            color={colors.error}
            colors={colors}
            selected={stockFilter === 'out-of-stock'}
            onClick={() => selectStockCard('out-of-stock')}
          />
        </Grid>
      </Grid>

      {/* ── Filter Bar ──────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" useFlexGap>
          <TextField
            size="small"
            placeholder="Search name, SKU, or barcode..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} /></InputAdornment>,
            }}
            sx={{
              minWidth: 280,
              '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
            }}
          />
          {/* Category — dropdown you can also type into */}
          <FilterAutocomplete
            label="Category"
            placeholder="All Categories"
            options={categories}
            value={categoryFilter}
            onChange={v => { setCategoryFilter(v); setPage(1); }}
            colors={colors}
          />

          {/* Tag — same, to Category's right */}
          <FilterAutocomplete
            label="Tag"
            placeholder="All Tags"
            options={tags}
            value={tagFilter}
            onChange={v => { setTagFilter(v); setPage(1); }}
            colors={colors}
          />
          {/* Which store the Stock column is reporting. Sits immediately left of
              the stock filter because the filter is meaningless without knowing
              what "in stock" is being measured against. */}
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Stock at</InputLabel>
            <Select
              value={storeFilter}
              onChange={e => { setStoreFilter(e.target.value as string); setPage(1); }}
              label="Stock at"
              sx={{
                bgcolor: colors.bgElevated, color: colors.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '& .MuiSelect-icon': { color: colors.textSecondary },
              }}
            >
              <MenuItem value={ALL_STORES}>All stores (total)</MenuItem>
              {stores.map((st) => (
                <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Stock</InputLabel>
            <Select
              value={stockFilter}
              onChange={e => { setStockFilter(e.target.value as string); setPage(1); }}
              label="Stock"
              sx={{
                bgcolor: colors.bgElevated, color: colors.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '& .MuiSelect-icon': { color: colors.textSecondary },
              }}
            >
              <MenuItem value="all">All Stock</MenuItem>
              <MenuItem value="in-stock">In Stock</MenuItem>
              <MenuItem value="out-of-stock">Out of Stock</MenuItem>
              <MenuItem value="low-stock">Low Stock (&le;5)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Sellable</InputLabel>
            <Select
              value={sellableFilter}
              onChange={e => { setSellableFilter(e.target.value as string); setPage(1); }}
              label="Sellable"
              sx={{
                bgcolor: colors.bgElevated, color: colors.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '& .MuiSelect-icon': { color: colors.textSecondary },
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Sellable</MenuItem>
              <MenuItem value="false">Not Sellable</MenuItem>
            </Select>
          </FormControl>

          <YesNoFilter
            label="New & Popular"
            value={popularFilter}
            onChange={v => { setPopularFilter(v); setPage(1); }}
            colors={colors}
          />

          <YesNoFilter
            label="Label"
            value={labelFilter}
            onChange={v => { setLabelFilter(v); setPage(1); }}
            colors={colors}
          />

          {(search || categoryFilter || tagFilter || stockFilter !== 'all' || storeFilter !== ALL_STORES || sellableFilter || popularFilter || labelFilter) && (
            <Button
              size="small"
              onClick={() => { setSearch(''); setCategoryFilter(''); setTagFilter(''); setStockFilter('all'); setStoreFilter(ALL_STORES); setSellableFilter(''); setPopularFilter(''); setLabelFilter(''); setPage(1); }}
              sx={{ color: colors.textSecondary, textTransform: 'none' }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Paper>
      </Box>

      {/* ── Product Table ───────────────────────────────────────────────── */}
      {/* maxHeight makes the container the scroller, which is what `stickyHeader`
          pins the header row to — without it the header scrolls away with the page. */}
      <TableContainer
        ref={listTopRef}
        component={Paper}
        sx={{
          bgcolor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          maxWidth: '100%',
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 300px)',
          minHeight: 320,
          // Sticky cells are transparent by default and would show rows through.
          '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: colors.bgCard },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colors.textMuted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderColor: colors.border, width: 50 }}>
                Image
              </TableCell>
              {/* Sortable columns. Tags is interleaved after Price (and isn't
                  sortable — it's a multi-value field), so the map is split. */}
              {[
                { key: 'sku', label: 'SKU' },
                { key: 'name', label: 'Name' },
                { key: 'category', label: 'Category' },
                // Vaperite (Jason, 4 Nov 2026): Price = Lightspeed's, Deeliver Price = what the app charges
                ...(vendOrg === 'vaperite'
                  ? [{ key: 'vendPrice', label: 'Price' }, { key: 'price', label: 'Deeliver Price' }]
                  : [{ key: 'price', label: 'Price' }]),
                { key: 'tags', label: 'Tags', sortable: false },
                { key: 'stock', label: storeFilter === ALL_STORES ? 'Stock (all stores)' : 'Stock (this store)' },
              ].map(col => (
                <TableCell
                  key={col.key}
                  onClick={col.sortable === false ? undefined : () => handleSort(col.key)}
                  sx={{
                    color: (sortBy === col.key || (col.key === 'price' && sortBy === 'priceLocked')) ? colors.info : colors.textMuted,
                    fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderColor: colors.border,
                    cursor: col.sortable === false ? 'default' : 'pointer',
                    ...(col.sortable === false ? {} : { '&:hover': { color: colors.info } }),
                    ...(col.key === 'name' ? { minWidth: 200 } : {}),
                    ...(col.key === 'tags' ? { minWidth: 160 } : {}),
                  }}
                >
                  {col.label}{col.sortable === false ? null : sortArrow(col.key)}
                </TableCell>
              ))}
              {/* The flag columns sort too (Jason, 4 Nov 2026): Yes first, then No; Label A-Z with blanks last. */}
              {[
                { key: 'sellable', label: 'Sellable' },
                { key: 'restricted', label: 'Restricted' },
                { key: 'popular', label: 'Popular' },
                { key: 'label', label: 'Label' },
                { key: 'anythingElse', label: 'Anything Else' },
              ].map(col => (
                <TableCell
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  sx={{
                    color: sortBy === col.key ? colors.info : colors.textMuted,
                    fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderColor: colors.border,
                    cursor: 'pointer', '&:hover': { color: colors.info }, whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}{sortArrow(col.key)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: vendOrg === 'vaperite' ? 13 : 12 }).map((_, j) => (
                    <TableCell key={j} sx={{ borderColor: colors.border }}>
                      <Skeleton sx={{ bgcolor: colors.bgElevated }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={vendOrg === 'vaperite' ? 13 : 12} sx={{ textAlign: 'center', py: 6, color: colors.textMuted, borderColor: colors.border }}>
                  No products found
                </TableCell>
              </TableRow>
            ) : (
              products.map(product => (
                <TableRow
                  key={product.id}
                  onClick={() => openEditDialog(product)}
                  sx={{ '&:hover': { bgcolor: colors.bgElevated }, cursor: 'pointer' }}
                >
                  {/* Image */}
                  <TableCell sx={{ borderColor: colors.border, py: 0.5 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1, overflow: 'hidden',
                      bgcolor: colors.bgElevated,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {product.imageUrl ? (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          loading="lazy"
                          decoding="async"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <StorefrontIcon sx={{ fontSize: 20, color: colors.textMuted }} />
                      )}
                    </Box>
                  </TableCell>
                  {/* SKU */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: colors.textPrimary }}>
                      {product.sku}
                    </Typography>
                  </TableCell>
                  {/* Name */}
                  <TableCell sx={{ borderColor: colors.border, maxWidth: 260 }}>
                    <Typography noWrap sx={{ fontSize: '0.85rem', color: colors.textPrimary }}>
                      {product.name}
                    </Typography>
                  </TableCell>
                  {/* Category */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Chip
                      label={product.category}
                      size="small"
                      sx={{
                        bgcolor: alpha(colors.info, 0.1),
                        color: colors.info,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  </TableCell>
                  {/* Price */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Typography sx={{ fontSize: '0.85rem', color: colors.textPrimary, fontFamily: 'monospace' }}>
                      {vendOrg === 'vaperite'
                        ? (product.vendPrice != null ? formatCurrency(product.vendPrice) : '—')
                        : formatCurrency(product.priceTier1)}
                    </Typography>
                  </TableCell>
                  {vendOrg === 'vaperite' && (
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Typography sx={{ fontSize: '0.85rem', color: colors.textPrimary, fontFamily: 'monospace', fontWeight: 600 }}>
                        {formatCurrency(product.priceTier1)}
                        {product.overrides?.price && (
                          <Typography component="span" title="Set by hand: ignoring syncs and the pricing rule" sx={{ ml: 0.5, fontSize: '0.7rem', color: colors.warning, fontWeight: 700, fontFamily: 'inherit' }}>(SET)</Typography>
                        )}
                      </Typography>
                    </TableCell>
                  )}
                  {/* Tags — same chip vibe as Category. Caps the visible chips so a
                      heavily-tagged product can't blow the row height out. */}
                  <TableCell sx={{ borderColor: colors.border, maxWidth: 220 }}>
                    {product.tags?.length ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {product.tags.slice(0, MAX_VISIBLE_TAGS).map(tag => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: alpha(colors.purple, 0.1),
                              color: colors.purple,
                              fontSize: '0.7rem',
                              height: 22,
                            }}
                          />
                        ))}
                        {product.tags.length > MAX_VISIBLE_TAGS && (
                          <Tooltip title={product.tags.slice(MAX_VISIBLE_TAGS).join(', ')}>
                            <Chip
                              label={`+${product.tags.length - MAX_VISIBLE_TAGS}`}
                              size="small"
                              sx={{
                                bgcolor: alpha(colors.textMuted, 0.12),
                                color: colors.textSecondary,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>—</Typography>
                    )}
                  </TableCell>
                  {/* Stock */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Typography sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: getStockColor(product.stock?.available, colors),
                    }}>
                      {product.stock ? product.stock.available : '—'}
                    </Typography>
                  </TableCell>
                  {/* Sellable */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Chip
                      label={product.meta.isSellableInApp ? 'Yes' : 'No'}
                      size="small"
                      sx={{
                        bgcolor: alpha(product.meta.isSellableInApp ? colors.success : colors.textMuted, 0.12),
                        color: product.meta.isSellableInApp ? colors.success : colors.textMuted,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  </TableCell>
                  {/* Restricted */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <YesNoChip on={product.meta.isRestricted} colors={colors} />
                  </TableCell>
                  {/* Popular */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <YesNoChip on={product.meta.isPopular} colors={colors} />
                  </TableCell>
                  {/* Label */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    {product.meta.label && (
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                        {product.meta.label}
                      </Typography>
                    )}
                  </TableCell>
                  {/* Anything Else */}
                  <TableCell sx={{ borderColor: colors.border }}>
                    <YesNoChip on={product.meta.isAnythingElse} colors={colors} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            sx={{
              '& .MuiPaginationItem-root': {
                color: colors.textSecondary,
                '&.Mui-selected': { bgcolor: alpha(colors.info, 0.2), color: colors.info },
              },
            }}
          />
        </Box>
      )}

      {/* ── Edit Product Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={!!editProduct}
        onClose={() => { if (!editSaving) { setEditProduct(null); setEditingField(null); setYmlSearching(null); } }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: {
          bgcolor: colors.bgDeepest,
          color: colors.textPrimary,
          border: `1px solid ${colors.border}`,
          borderRadius: 3,
          overflow: 'hidden',
        } }}
      >
        {editProduct && (
          <>
            {/* ── Hero Header ──────────────────────────────────────────── */}
            <Box sx={{
              position: 'relative',
              display: 'flex',
              gap: 3,
              p: 3,
              pb: 2.5,
              background: `linear-gradient(135deg, ${colors.bgCard} 0%, ${alpha(colors.info, 0.06)} 100%)`,
              borderBottom: `1px solid ${colors.border}`,
            }}>
              {/* Product image */}
              <Box sx={{
                width: 110, height: 110, flexShrink: 0,
                borderRadius: 3,
                bgcolor: colors.bgElevated,
                border: `1px solid ${colors.border}`,
                overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 20px ${alpha('#000', 0.4)}`,
              }}>
                {editProduct.imageUrl ? (
                  <Box component="a" href={editProduct.imageUrl} target="_blank" rel="noopener" title="Open full size" sx={{ display: 'block', width: '100%', height: '100%' }}>
                    <Box component="img" src={editProduct.imageUrl} alt={editProduct.name} decoding="async" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ) : (
                  <StorefrontIcon sx={{ fontSize: 40, color: colors.textMuted }} />
                )}
              </Box>

              {/* Product info */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.5 }}>{editProduct.name}</Typography>
                {lockedChip('name')}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: colors.textMuted }}>{editProduct.sku}</Typography>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: colors.textMuted }} />
                  <Chip
                    label={editProduct.category}
                    size="small"
                    sx={{ bgcolor: alpha(colors.info, 0.1), color: colors.info, fontSize: '0.7rem', height: 22 }}
                  />
                  <Chip
                    label={editProduct.status}
                    size="small"
                    sx={{
                      bgcolor: alpha(editProduct.status === 'Active' ? colors.success : colors.textMuted, 0.12),
                      color: editProduct.status === 'Active' ? colors.success : colors.textMuted,
                      fontSize: '0.7rem', height: 22,
                    }}
                  />
                </Stack>

                {/* The one stock number a product has. Per-branch split is the
                    green Stock button, top right. */}
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                    Available
                  </Typography>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: 'monospace', color: getStockColor(editProduct.stock?.available, colors), lineHeight: 1.3 }}>
                    {editProduct.stock?.available ?? '—'}
                  </Typography>
                </Box>
              </Box>

              {/* Where this product's stock actually sits, branch by branch.
                  Read-only — stock is POS-owned, so this answers "where is
                  it?", not "change it". */}
              <Button
                onClick={() => openStockByStore(editProduct)}
                disabled={editSaving}
                variant="contained"
                size="small"
                startIcon={<StockIcon />}
                sx={{
                  position: 'absolute', top: 16, right: 16,
                  bgcolor: colors.success, color: '#fff', textTransform: 'none', fontWeight: 700,
                  boxShadow: `0 0 12px ${alpha(colors.success, 0.35)}`,
                  '&:hover': { bgcolor: alpha(colors.success, 0.85) },
                }}
              >
                Stock
              </Button>
            </Box>

            <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
              {/* ── Pricing Row ────────────────────────────────────────── */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Pricing" />
                {/* Jason, 5 Nov 2026: Lightspeed price first, then the Deeliver price with its
                    "Don't override" tick and note directly under it. Vaperite's products and ours alike. */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem', fontWeight: 600 }}>
                      Lightspeed price
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', color: colors.textPrimary }}>
                      {editProduct.vendPrice != null ? formatCurrency(editProduct.vendPrice) : 'not synced yet'}
                      {vendOrg === 'vaperite' && editProduct.vendPrice != null && pricingRule && (
                        <Typography component="span" sx={{ ml: 1.5, fontSize: '0.8rem', color: colors.textMuted, fontFamily: 'inherit' }}>
                          {pricingRule.enabled
                            ? `rule gives ${formatCurrency(rulePrice(editProduct.vendPrice) ?? 0)}`
                            : 'rule off: Deeliver price follows it'}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <EditableField
                      label="Deeliver price"
                      value={editForm.priceTier1 ?? ''}
                      displayValue={formatCurrency(editForm.priceTier1 || 0)}
                      editing={editingField === 'price'}
                      onStartEdit={() => setEditingField('price')}
                      onChange={v => setEditForm({ ...editForm, priceTier1: v === '' ? '' : Number(v), priceLocked: true })}
                      onDone={() => setEditingField(null)}
                      type="number"
                      mono
                    />
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      <FormControlLabel
                        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: colors.textPrimary } }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={!!editForm.priceLocked}
                            onChange={(e) => setEditForm({ ...editForm, priceLocked: e.target.checked })}
                            slotProps={{ input: { 'aria-label': "Don't override the Deeliver price" } }}
                          />
                        )}
                        label="Don't override (ignore syncs)"
                      />
                      {editForm.priceLocked && (
                        <Chip label="IGNORING SYNCS" size="small" sx={{ bgcolor: alpha(colors.warning, 0.15), color: colors.warning, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {editForm.priceLocked
                        ? (vendOrg === 'vaperite'
                          ? 'Set by hand: Lightspeed price changes and the pricing rule leave this product alone. Untick to follow Lightspeed and the rule again (the price goes back on save).'
                          : 'Set by hand: Lightspeed price changes leave this product alone. Untick to follow Lightspeed again (the price goes back on save).')
                        : (vendOrg === 'vaperite'
                          ? 'Follows Lightspeed and the pricing rule. Typing a Deeliver price sets it by hand and ticks this.'
                          : 'Follows Lightspeed. Typing a Deeliver price sets it by hand and ticks this.')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <GlowDivider />

              {/* ── Basic Fields ───────────────────────────────────────── */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Product Details" />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <EditableField
                      label="SKU (Vend — read only)"
                      value={editForm.sku || ''}
                      editing={false}
                      onStartEdit={() => {}}
                      onChange={() => {}}
                      onDone={() => {}}
                      mono
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <FieldDisplay label="Barcode" value={editProduct.barcode || '—'} mono />
                    {lockedChip('barcode')}
                  </Grid>
                  {/* Brand = Lightspeed's; Deeliver Brand = what Brand Mappings made of it (product.brand),
                      shown only when the mapping changed it. Neither is edited here. */}
                  <Grid size={{ xs: 6, md: 2 }}>
                    <FieldDisplay label="Brand" value={(editProduct.lightspeedBrand ?? editProduct.brand) || '—'} />
                    {lockedChip('brand')}
                  </Grid>
                  {editProduct.lightspeedBrand && editProduct.brand && brandKey(editProduct.lightspeedBrand) !== brandKey(editProduct.brand) && (
                    <Grid size={{ xs: 6, md: 2 }}>
                      <FieldDisplay label="Deeliver Brand" value={editProduct.brand} />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <GlowDivider />

              {/* ── Descriptions ───────────────────────────────────────── */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Description" />
                <EditableField
                  label="Description"
                  value={editForm.description || ''}
                  editing={editingField === 'desc'}
                  onStartEdit={() => setEditingField('desc')}
                  onChange={v => setEditForm({ ...editForm, description: v, descriptionLocked: true })}
                  onDone={() => setEditingField(null)}
                  multiline
                />
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <FormControlLabel
                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: colors.textPrimary } }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={!!editForm.descriptionLocked}
                        onChange={(e) => setEditForm({ ...editForm, descriptionLocked: e.target.checked })}
                        slotProps={{ input: { 'aria-label': "Don't override the description" } }}
                      />
                    )}
                    label="Don't override (ignore syncs)"
                  />
                  {editForm.descriptionLocked && (
                    <Chip label="IGNORING SYNCS" size="small" sx={{ bgcolor: alpha(colors.warning, 0.15), color: colors.warning, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                  )}
                </Stack>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {editForm.descriptionLocked
                    ? 'Set by hand: Lightspeed description changes leave this product alone. Untick to follow Lightspeed again (the text goes back on save).'
                    : 'Follows Lightspeed (approved through Item Staging). Typing a description sets it by hand and ticks this.'}
                </Typography>
              </Box>

              <GlowDivider />

              {/* ── Category ───────────────────────────────────────────── */}
              {/* Presented like Tags — its own section, click to edit. Unlike Tags
                  it's a fixed list: the backend maps the name back to a category_id
                  and rejects anything that isn't a real category. */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Category" />
                <FieldDisplay label="Category" value={editProduct.category || '—'} />
                {lockedChip('category')}
              </Box>

              <GlowDivider />

              {/* ── Tags ───────────────────────────────────────────────── */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Tags" />
                <FieldDisplay label="Tags" value={editProduct.tags.join(', ') || '—'} />
              </Box>

              <GlowDivider />

              {/* ── Meta Tags ──────────────────────────────────────────── */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Meta Tags" />
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {[
                    { key: 'isSellableInApp', label: 'Sellable in App', color: colors.success },
                    { key: 'isRestricted', label: 'Restricted (18+)', color: colors.error },
                    { key: 'isPopular', label: 'New & Popular', color: colors.warning },
                    { key: 'isAnythingElse', label: 'Anything Else', color: colors.purple },
                  ].map(tag => {
                    const checked = editForm.meta?.[tag.key] ?? false;
                    return (
                      <Grid size={{ xs: 6, md: 3 }} key={tag.key}>
                        <Box
                          onClick={() => setEditForm({ ...editForm, meta: { ...editForm.meta, [tag.key]: !checked } })}
                          sx={{
                            p: 1.5, borderRadius: 2, cursor: 'pointer',
                            bgcolor: checked ? alpha(tag.color, 0.08) : colors.bgCard,
                            border: `1px solid ${checked ? alpha(tag.color, 0.4) : colors.border}`,
                            boxShadow: checked ? `0 0 12px ${alpha(tag.color, 0.1)}` : 'none',
                            transition: 'all 0.2s',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            '&:hover': { borderColor: alpha(tag.color, 0.5), bgcolor: alpha(tag.color, 0.05) },
                          }}
                        >
                          <Box sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: checked ? tag.color : colors.textMuted,
                            boxShadow: checked ? `0 0 8px ${tag.color}` : 'none',
                            transition: 'all 0.2s',
                          }} />
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: checked ? 600 : 400, color: checked ? tag.color : colors.textSecondary }}>
                            {tag.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                {/* Sell in app overridden (Vaperite): ours, kept apart from their woocommerce tag. */}
                {(() => {
                  const o = overrideProps('sellable').override;
                  if (!o) return null;
                  return (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} data-field="sellable">
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>Sellable in App</Typography>
                      <Tooltip title={o.tooltip}>
                        <Chip
                          data-testid="overridden-sellable"
                          label="Overridden"
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem', fontWeight: 700,
                            bgcolor: alpha(colors.warning, 0.12), color: colors.warning,
                            border: `1px solid ${alpha(colors.warning, 0.3)}`,
                          }}
                        />
                      </Tooltip>
                      <Button
                        size="small"
                        disabled={o.busy}
                        onClick={o.onFollow}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0, minWidth: 0, color: colors.info }}
                      >
                        Follow Lightspeed again
                      </Button>
                    </Stack>
                  );
                })()}
                {/* Label is a badge, not a sentence. The DB column is VARCHAR(255)
                    with no validation, so cap it here or a pasted paragraph will
                    wreck every table row it appears in. */}
                <EditableField
                  label={`Label (max ${MAX_LABEL_LENGTH} chars)`}
                  value={editForm.meta?.label || ''}
                  editing={editingField === 'label'}
                  onStartEdit={() => setEditingField('label')}
                  onChange={v => setEditForm({
                    ...editForm,
                    meta: { ...editForm.meta, label: v.slice(0, MAX_LABEL_LENGTH) },
                  })}
                  onDone={() => setEditingField(null)}
                />
              </Box>

              <GlowDivider />

              {/* ── Related SKUs (YML) — search-and-assign ─────────────── */}
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Related Products (YML)" />
                <Grid container spacing={2}>
                  {(['relatedSku1', 'relatedSku2', 'relatedSku3', 'relatedSku4'] as const).map((slot, i) => {
                    const currentSku = editForm.meta?.[slot] || '';
                    const isSearching = ymlSearching === slot;
                    return (
                      <Grid size={{ xs: 12, md: 6 }} key={slot}>
                        <Box sx={{
                          p: 1.5, borderRadius: 2,
                          bgcolor: colors.bgCard,
                          border: `1px solid ${isSearching ? colors.info : colors.border}`,
                          boxShadow: isSearching ? `0 0 12px ${alpha(colors.info, 0.1)}` : 'none',
                          transition: 'all 0.2s',
                        }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            YML {i + 1}
                          </Typography>
                          {currentSku && !isSearching ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: colors.textPrimary }}>
                                {currentSku}
                              </Typography>
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Change">
                                  <IconButton size="small" onClick={() => openYmlSearch(slot)} sx={{ color: colors.info, p: 0.25 }}>
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton size="small" onClick={() => clearYmlSlot(slot)} sx={{ color: colors.error, p: 0.25 }}>
                                    <CancelIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          ) : isSearching ? (
                            <Box
                              sx={{ mt: 0.5 }}
                              onBlur={e => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                  setYmlSearching(null);
                                  setYmlQuery('');
                                  setYmlResults([]);
                                }
                              }}
                            >
                              <TextField
                                placeholder="Search by name or SKU..."
                                value={ymlQuery}
                                onChange={e => setYmlQuery(e.target.value)}
                                autoFocus
                                fullWidth size="small"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} /></InputAdornment>,
                                  endAdornment: ymlLoading ? <CircularProgress size={14} /> : (
                                    <IconButton size="small" onClick={() => setYmlSearching(null)} sx={{ p: 0.25 }}>
                                      <CancelIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                                    </IconButton>
                                  ),
                                }}
                                sx={{
                                  '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, fontSize: '0.8rem' },
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                                }}
                              />
                              {ymlResults.length > 0 && (
                                <Paper sx={{
                                  mt: 0.5, maxHeight: 160, overflow: 'auto',
                                  bgcolor: colors.bgCard, border: `1px solid ${colors.border}`,
                                }}>
                                  {ymlResults
                                    .filter(r => r.sku !== editProduct.sku)
                                    .map(r => {
                                    // The search stays open across picks, so the
                                    // same result list is reused for the next
                                    // slot. Showing what is already taken stops
                                    // an accidental duplicate — four slots
                                    // holding the same product helps nobody.
                                    const taken = assignedYmlSkus.has(r.sku);
                                    return (
                                    <MenuItem
                                      key={r.sku}
                                      disabled={taken}
                                      onMouseDown={e => e.preventDefault()}
                                      onClick={() => { if (!taken) selectYmlProduct(slot, r.sku); }}
                                      sx={{
                                        py: 0.75, px: 1.5,
                                        opacity: taken ? 0.45 : 1,
                                        '&:hover': { bgcolor: taken ? 'transparent' : alpha(colors.info, 0.08) },
                                      }}
                                    >
                                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: colors.info, mr: 1.5, flexShrink: 0 }}>
                                        {r.sku}
                                      </Typography>
                                      <Typography noWrap sx={{ fontSize: '0.8rem', color: colors.textSecondary, flexGrow: 1 }}>
                                        {r.name}
                                      </Typography>
                                      {taken && (
                                        <Typography sx={{ fontSize: '0.65rem', color: colors.textMuted, ml: 1, flexShrink: 0 }}>
                                          added
                                        </Typography>
                                      )}
                                    </MenuItem>
                                    );
                                  })}
                                </Paper>
                              )}
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              onClick={() => openYmlSearch(slot)}
                              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                              sx={{ color: colors.textMuted, textTransform: 'none', mt: 0.5, fontSize: '0.75rem', p: 0 }}
                            >
                              Assign product
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {/* ── Images ─────────────────────────────────────────────── */}
              <GlowDivider />
              <Box sx={{ p: 2.5 }}>
                <SectionHeader label="Images" />
                {editProduct.allImages.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {editProduct.allImages.map((url, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 80, height: 80, borderRadius: 2, overflow: 'hidden',
                          border: i === 0 ? `2px solid ${colors.info}` : `1px solid ${colors.border}`,
                          boxShadow: i === 0 ? `0 0 10px ${alpha(colors.info, 0.2)}` : 'none',
                        }}
                      >
                        <Box component="a" href={url} target="_blank" rel="noopener" title="Open full size" sx={{ display: 'block', width: '100%', height: '100%' }}>
                          <Box component="img" src={url} alt={editProduct.name} loading="lazy" decoding="async" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    disabled={imageUploading}
                    startIcon={imageUploading ? <CircularProgress size={14} /> : <AddIcon />}
                    sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary }}
                  >
                    Upload image
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSetImage(f); e.currentTarget.value = ''; }}
                    />
                  </Button>
                  <TextField
                    size="small"
                    placeholder="…or paste an image URL"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={imageUploading || !newImageUrl.trim()}
                    onClick={() => handleSetImage()}
                    sx={{ textTransform: 'none' }}
                  >
                    Set URL
                  </Button>
                </Stack>
              </Box>

            </DialogContent>

            {/* ── Footer Actions ─────────────────────────────────────── */}
            <Box sx={{
              display: 'flex', justifyContent: 'flex-end', gap: 1.5,
              p: 2, px: 3,
              borderTop: `1px solid ${colors.border}`,
              background: `linear-gradient(180deg, ${colors.bgCard} 0%, ${colors.bgDeepest} 100%)`,
            }}>
              <Button
                onClick={() => { setEditProduct(null); setEditingField(null); setYmlSearching(null); }}
                disabled={editSaving}
                sx={{ color: colors.textSecondary, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSave}
                disabled={editSaving}
                variant="contained"
                startIcon={editSaving ? <CircularProgress size={16} /> : <CheckIcon />}
                sx={{
                  bgcolor: colors.info,
                  textTransform: 'none',
                  px: 3,
                  boxShadow: `0 0 15px ${alpha(colors.info, 0.3)}`,
                  '&:hover': { bgcolor: alpha(colors.info, 0.85), boxShadow: `0 0 25px ${alpha(colors.info, 0.4)}` },
                }}
              >
                {editSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      {/* ── Confirm Changes Dialog ─────────────────────────────────────── */}
      <Dialog
        open={!!confirmPayload}
        onClose={() => !editSaving && setConfirmPayload(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.bgCard, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(colors.warning, 0.12),
          }}>
            <EditIcon sx={{ fontSize: 20, color: colors.warning }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Confirm changes</Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              These are saved to the Deeliver catalogue
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: colors.border }}>
          {editProduct && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: colors.bgElevated, border: `1px solid ${colors.border}` }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{editProduct.name}</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: colors.textMuted }}>{editProduct.sku}</Typography>
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Changes to apply
          </Typography>
          {confirmPayload?.changes.map((change, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
              borderBottom: i < confirmPayload.changes.length - 1 ? `1px solid ${colors.border}` : 'none',
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.warning, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.85rem', color: colors.textPrimary }}>{change}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmPayload(null)} sx={{ color: colors.textSecondary, textTransform: 'none' }}>
            Go Back
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            startIcon={editSaving ? <CircularProgress size={16} /> : <CheckIcon />}
            disabled={editSaving}
            sx={{
              bgcolor: colors.warning, color: '#000', textTransform: 'none',
              '&:hover': { bgcolor: alpha(colors.warning, 0.85) },
            }}
          >
            {editSaving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Stock by Store Dialog ───────────────────────────────────────── */}
      <Dialog
        open={!!stockByStoreFor}
        onClose={() => setStockByStoreFor(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.bgCard, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(colors.info, 0.12),
          }}>
            <StockIcon sx={{ fontSize: 20, color: colors.info }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Stock by store</Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {stockByStoreFor?.sku} · read-only (stock is owned by the POS)
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: colors.border }}>
          {stockByStoreLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!stockByStoreLoading && stockByStore?.length === 0 && (
            <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', py: 2 }}>
              This product isn&apos;t assigned to any store. Nothing in
              <Box component="code" sx={{ mx: 0.5, fontFamily: 'monospace' }}>product_stock_by_store</Box>
              references it, so it won&apos;t appear in the app anywhere.
            </Typography>
          )}

          {!stockByStoreLoading && !!stockByStore?.length && (
            <>
              <Box sx={{ display: 'flex', px: 1, pb: 0.5 }}>
                <Typography sx={{ flex: 1, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textMuted }}>Store</Typography>
                <Typography sx={{ width: 96, textAlign: 'right', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textMuted }}>
                  Available
                </Typography>
              </Box>
              {stockByStore.map((row, i) => (
                <Box
                  key={row.storeId}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 1, py: 1,
                    borderTop: i === 0 ? `1px solid ${colors.border}` : 'none',
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: colors.textPrimary }}>{row.storeName}</Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: colors.textMuted }}>
                      {row.storeExternalId}
                    </Typography>
                  </Box>
                  <Typography sx={{ width: 96, textAlign: 'right', fontSize: '0.9rem', fontWeight: 700, fontFamily: 'monospace', color: getStockColor(row.available, colors) }}>
                    {row.available}
                  </Typography>
                </Box>
              ))}
              {/* The view is refreshed by webhooks/cron, so say how fresh it is —
                  a stale view is the usual reason the app disagrees with this. */}
              {stockByStore[0]?.lastRefreshedAt && (
                <Typography sx={{ mt: 1.5, fontSize: '0.7rem', color: colors.textMuted }}>
                  Stock view last refreshed {new Date(stockByStore[0].lastRefreshedAt).toLocaleString()}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, p: 2 }}>
          <Button onClick={() => setStockByStoreFor(null)} sx={{ color: colors.textSecondary, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Product dialog removed (#2) — products come from Vend via staging. */}
    </Box>
  );
};

// ============================================================================
// Section Header (glowing label for edit dialog sections)
// ============================================================================

function SectionHeader({ label }: { label: string }) {
  const { colors } = useAdminTheme();
  return (
    <Typography variant="subtitle2" sx={{
      color: colors.info,
      mb: 1.5,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.7rem',
      letterSpacing: '0.08em',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      '&::after': {
        content: '""',
        flex: 1,
        height: '1px',
        background: `linear-gradient(90deg, ${alpha(colors.info, 0.3)}, transparent)`,
      },
    }}>
      {label}
    </Typography>
  );
}

// ============================================================================
// Glow Divider (subtle gradient line between edit dialog sections)
// ============================================================================

function GlowDivider() {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{
      height: '1px',
      mx: 2.5,
      background: `linear-gradient(90deg, transparent, ${alpha(colors.border, 0.8)}, transparent)`,
    }} />
  );
}

// ============================================================================
// Click-to-Edit Field Component
// ============================================================================

interface EditableFieldProps {
  label: string;
  value: string | number;
  displayValue?: string;
  editing: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onDone: () => void;
  multiline?: boolean;
  type?: string;
  mono?: boolean;
  /**
   * Constrain editing to a fixed list (renders a Select instead of a TextField).
   *
   * Category needs this: the backend resolves the submitted NAME back to a
   * category_id, so a free-typed value that doesn't match a real category is
   * rejected on save. Tags stay free text — they're arbitrary by design.
   */
  options?: string[];
  /** Client-side cap on the text input. */
  maxLength?: number;
  /**
   * A Vaperite field override: shows an "Overridden" chip (tooltip = the Lightspeed
   * value) and a "Follow Lightspeed again" action.
   */
  override?: { field: string; tooltip: string; busy?: boolean; onFollow: () => void };
}

function EditableField({ label, value, displayValue, editing, onStartEdit, onChange, onDone, multiline, type, mono, options, maxLength, override }: EditableFieldProps) {
  const { colors } = useAdminTheme();
  if (!override) {
    return (
      <EditableFieldBody {...{ label, value, displayValue, editing, onStartEdit, onChange, onDone, multiline, type, mono, options, maxLength }} />
    );
  }
  return (
    <Box data-field={override.field}>
      <EditableFieldBody {...{ label, value, displayValue, editing, onStartEdit, onChange, onDone, multiline, type, mono, options, maxLength }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
        <Tooltip title={override.tooltip}>
          <Chip
            data-testid={`overridden-${override.field}`}
            label="Overridden"
            size="small"
            sx={{
              height: 20, fontSize: '0.65rem', fontWeight: 700,
              bgcolor: alpha(colors.warning, 0.12), color: colors.warning,
              border: `1px solid ${alpha(colors.warning, 0.3)}`,
            }}
          />
        </Tooltip>
        <Button
          size="small"
          disabled={override.busy}
          onClick={(e) => { e.stopPropagation(); override.onFollow(); }}
          sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0, minWidth: 0, color: colors.info }}
        >
          Follow Lightspeed again
        </Button>
      </Stack>
    </Box>
  );
}

function EditableFieldBody({ label, value, displayValue, editing, onStartEdit, onChange, onDone, multiline, type, mono, options, maxLength }: Omit<EditableFieldProps, 'override'>) {
  const { colors } = useAdminTheme();

  if (editing && options) {
    return (
      <TextField
        select
        label={label}
        value={String(value ?? '')}
        onChange={e => { onChange(e.target.value); onDone(); }}
        onBlur={onDone}
        autoFocus
        fullWidth
        size="small"
        SelectProps={{ native: false, defaultOpen: true }}
        sx={{
          '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
          '& .MuiInputLabel-root': { color: colors.info },
        }}
      >
        {options.map(opt => (
          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
        ))}
      </TextField>
    );
  }

  if (editing) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onDone}
        onKeyDown={e => { if (e.key === 'Enter' && !multiline) onDone(); }}
        autoFocus
        fullWidth
        size="small"
        type={type}
        inputProps={maxLength ? { maxLength } : undefined}
        multiline={multiline}
        rows={multiline ? 4 : undefined}
        sx={{
          '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
          '& .MuiInputLabel-root': { color: colors.info },
        }}
      />
    );
  }

  const display = displayValue || String(value || '—');
  const isEmpty = !value && value !== 0;

  return (
    <Box
      onClick={onStartEdit}
      sx={{
        cursor: 'pointer',
        p: 1, borderRadius: 1,
        border: `1px solid transparent`,
        '&:hover': { bgcolor: alpha(colors.info, 0.04), borderColor: alpha(colors.info, 0.2) },
        transition: 'all 0.15s',
      }}
    >
      <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{
        color: isEmpty ? colors.textMuted : colors.textPrimary,
        fontSize: multiline ? '0.85rem' : '0.9rem',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontStyle: isEmpty ? 'italic' : 'normal',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        wordBreak: multiline ? 'break-word' : undefined,
      }}>
        {isEmpty ? 'Click to edit' : display}
      </Typography>
    </Box>
  );
}

// ============================================================================
// Read-Only Field Display
// ============================================================================

function FieldDisplay({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', fontFamily: mono ? 'monospace' : 'inherit' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default ProductManagement;
